/**
 * Reportes formales — issue #47 (B6.11).
 *
 * Se elige un período, se genera, y lo que sale es un documento con firma:
 * marca, período, quién lo generó, cuándo, y una referencia. El botón de
 * imprimir produce el PDF con esos datos adentro.
 *
 * El período se fija **al generar**, no en cada render: un documento que
 * cambia de contenido mientras alguien lo mira no es un documento. Por eso
 * `issued` guarda el período y la firma juntos, y las consultas cuelgan de
 * ese objeto.
 *
 * La **programación por correo** (semanal y mensual) vive en `SchedulesPanel`,
 * abajo de todo: ya existen la tabla y los endpoints (#70). Ahí el documento
 * lo genera el servidor y sale por correo sin que nadie esté mirando, que es
 * lo que esta pantalla no puede hacer — su PDF sale de `window.print()`.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import type { ReportPeriod } from '@/api/keys';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { useTenantSettings } from '@/tenant/useTenantSettings';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { cn } from '@/ui/cn';
import { ExportsPanel } from './ExportsPanel';
import { ReportDocument, type ReportSignature } from './ReportDocument';
import { SchedulesPanel } from './SchedulesPanel';

interface Issued {
  period: ReportPeriod;
  signature: ReportSignature;
}

const PRESETS = [
  { days: 7, key: 'reports.period.last7' },
  { days: 30, key: 'reports.period.last30' },
  { days: 90, key: 'reports.period.last90' },
] as const;

export function ReportsPage(): ReactNode {
  const { t } = useI18n();
  const { claims } = useAuth();
  const tenant = useTenantSettings();

  const [since, setSince] = useState(() => isoDaysAgo(30));
  const [until, setUntil] = useState(() => isoDaysAgo(0));
  const [invalid, setInvalid] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);

  const applyPreset = (days: number): void => {
    setSince(isoDaysAgo(days));
    setUntil(isoDaysAgo(0));
    setInvalid(false);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (since > until) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (claims === null) return;
    setIssued({
      period: { since, until },
      signature: {
        brand: tenant.name,
        tenantSlug: claims.tenant,
        name: claims.name ?? claims.email,
        email: claims.email,
        role: claims.role,
        generatedAt: new Date().toISOString(),
        reference: newReference(),
      },
    });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Card>
        <CardHeader title={t('reports.title')} description={t('reports.description')} />
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.days}
                  size="sm"
                  variant="secondary"
                  className={cn(since === isoDaysAgo(preset.days) && until === isoDaysAgo(0) && 'border-accent text-accent')}
                  onClick={() => {
                    applyPreset(preset.days);
                  }}
                >
                  {t(preset.key)}
                </Button>
              ))}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-ink-muted">{t('reports.form.from')}</span>
                <input
                  type="date"
                  value={since}
                  onChange={(event) => {
                    setSince(event.target.value);
                  }}
                  className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                />
              </label>
              <label className="text-sm">
                <span className="block text-ink-muted">{t('reports.form.to')}</span>
                <input
                  type="date"
                  value={until}
                  onChange={(event) => {
                    setUntil(event.target.value);
                  }}
                  className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                />
              </label>
              <Button type="submit" variant="primary">
                {issued === null ? t('reports.form.generate') : t('reports.form.regenerate')}
              </Button>
            </div>

            {invalid && (
              <p role="alert" className="text-sm text-critical">
                {t('reports.form.invalidRange')}
              </p>
            )}
          </form>
        </CardBody>
      </Card>

      {issued !== null && (
        <ReportDocument period={issued.period} signature={issued.signature} />
      )}

      <ExportsPanel period={{ since, until }} />

      {/* La programación va última y en la MISMA página, no en una vista
          nueva: el flujo real es «genero el de este mes … y además, mandámelo
          todos los meses». Una pantalla aparte separaría dos mitades de la
          misma decisión — y el proyecto ya tiene dos interfaces de
          exportación por haber construido en paralelo. */}
      <SchedulesPanel />
    </div>
  );
}

/** `AAAA-MM-DD` de hace `days` días, en UTC — que es como los toma la API. */
function isoDaysAgo(days: number): string {
  const date = new Date(Date.now() - days * 86_400_000);
  return date.toISOString().slice(0, 10);
}

/**
 * Referencia del documento. No pretende ser un identificador del servidor:
 * es lo que se cita en un correo o en un acta ("el reporte RPT-…") para que
 * dos personas sepan que están hablando del mismo papel.
 */
function newReference(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  const suffix = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
  return `RPT-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${suffix}`;
}
